import { Button, Group, SimpleGrid, Text } from "@mantine/core";

/**
 * Large-touch-target numeric pad for entering 4–6 digit PINs at the kiosk.
 * Stateless: parent owns the pin string. Designed for the staff-override
 * modal (cancel-mid-session with items scanned, future force-complete).
 *
 * The pin is rendered as • dots so a shoulder-surfer can't read it off the
 * tablet. Clear (←) removes the last digit; long-press ← is intentionally
 * not supported (slower deliberate UX wins here over keyboard-app polish).
 */
export interface PinPadProps {
  value: string;
  onChange: (next: string) => void;
  maxLength?: number;
  disabled?: boolean;
}

const KEYS: Array<string | "back" | "blank"> = [
  "1", "2", "3",
  "4", "5", "6",
  "7", "8", "9",
  "blank", "0", "back",
];

export function PinPad({
  value,
  onChange,
  maxLength = 6,
  disabled,
}: PinPadProps) {
  const append = (digit: string) => {
    if (disabled) return;
    if (value.length >= maxLength) return;
    onChange(value + digit);
  };
  const back = () => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  };

  const masked = "•".repeat(value.length).padEnd(4, "·");

  return (
    <div>
      <Group position="center" mb="sm">
        <Text size="xl" weight={700} sx={{ letterSpacing: 6 }}>
          {masked}
        </Text>
      </Group>
      <SimpleGrid cols={3} spacing="xs">
        {KEYS.map((k, i) => {
          if (k === "blank") return <div key={i} />;
          if (k === "back") {
            return (
              <Button
                key={i}
                size="xl"
                variant="light"
                color="gray"
                onClick={back}
                disabled={disabled || value.length === 0}
                aria-label="Delete digit"
              >
                ←
              </Button>
            );
          }
          return (
            <Button
              key={i}
              size="xl"
              variant="default"
              onClick={() => append(k)}
              disabled={disabled}
              aria-label={`Digit ${k}`}
            >
              {k}
            </Button>
          );
        })}
      </SimpleGrid>
    </div>
  );
}
